// apps/provider/src/services/api/patients.js
// Patient CRUD, search, invite, matching, and verification methods
//
// Methods: getAllPatients, getPatient, createPatient, updatePatient, deletePatient,
//          sendPatientInvite, getPatientInvites, sendPatientIntake, verifyPatientByDob,
//          createPatientPublic, sendVerificationCode, verifyCode, getPendingPatientUpdates,
//          approvePatientUpdate, rejectPatientUpdate, findExistingPatient,
//          createOrUpdatePatientFromForm, searchPatients, searchPatientsExtension,
//          getPatientDiagnoses, getPatientVisitLimitUsage
//
// Response types: @prism-ehr/shared → Patient, PendingPatientUpdate, PatientMatchResult, PatientVerificationResult

/**
 * @returns {Object} Patients domain methods (bound to `this` of the composed apiService)
 */
export function createPatientsMethods() {
  return {
    /**
     * Get all patients for an organization.
     *
     * Xano: GET /patient
     * Xano file: 472_patient_GET.xs
     *
     * @param {string} organization_id
     * @param {Object} [filters]
     * @param {string[]} [filters.location_ids] - Filter to patients at these locations
     * @returns {Promise<import('@prism-ehr/shared').Patient[]>}
     */
    async getAllPatients(organization_id, filters = {}) {
      const queryParams = new URLSearchParams({ organization_id });
      if (filters.location_ids?.length > 0) {
        queryParams.append('location_ids', filters.location_ids.join(','));
      }
      return this.request(`/patient?${queryParams.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Get paginated patients with server-side search, sort, and filtering.
     *
     * Xano: GET /patient-with-pagination
     * Xano file: patient-with-pagination_GET.xs
     *
     * @param {Object} params
     * @param {string} params.organization_id
     * @param {string} [params.location_ids] - Comma-separated location IDs
     * @param {string} [params.search_name] - Name search term (pre-parsed, no date)
     * @param {string} [params.search_dob] - DOB filter in YYYY-MM-DD format (pre-parsed)
     * @param {string} [params.case_status] - active|discharged|on_hold|cancelled
     * @param {string} [params.case_care_setting] - outpatient|home_health
     * @param {number} [params.page=1]
     * @param {number} [params.per_page=25]
     * @returns {Promise<import('@prism-ehr/shared').PaginatedResponse<import('@prism-ehr/shared').Patient>>}
     */
    async getPatientsWithPagination({ organization_id, location_ids, search_name, search_dob, case_status, case_care_setting, page = 1, per_page = 25 }, options = {}) {
      const queryParams = new URLSearchParams({ organization_id, page, per_page });
      if (location_ids) queryParams.append('location_ids', location_ids);
      if (search_name) queryParams.append('search_name', search_name);
      if (search_dob) queryParams.append('search_dob', search_dob);
      if (case_status) queryParams.append('case_status', case_status);
      if (case_care_setting) queryParams.append('case_care_setting', case_care_setting);
      return this.request(`/patient-with-pagination?${queryParams.toString()}`, {
        method: 'GET',
        signal: options.signal
      });
    },

    /**
     * Get a single patient by ID.
     *
     * Xano: GET /patient/{patientId}
     * Xano file: 471_patient_patient_id_GET.xs
     *
     * @param {string} patientId
     * @returns {Promise<import('@prism-ehr/shared').Patient>}
     */
    async getPatient(patientId) {
      return this.request(`/patient/${patientId}`, {
        method: 'GET'
      });
    },

    /**
     * Create a new patient.
     *
     * Xano: POST /patient
     * Xano file: 473_patient_POST.xs
     *
     * @param {Object} patientData
     * @returns {Promise<import('@prism-ehr/shared').Patient>}
     */
    async createPatient(patientData) {
      return this.request('/patient', {
        method: 'POST',
        body: JSON.stringify(patientData)
      });
    },

    async checkDuplicatePatient({ organization_id, first_name, last_name, birthday }) {
      const params = new URLSearchParams({ organization_id, first_name, last_name, birthday });
      return this.request(`/patient-duplicate-check?${params.toString()}`, { method: 'GET' });
    },

    /**
     * Update an existing patient.
     *
     * Xano: PATCH /patient/{patientId}
     * Xano file: 474_patient_patient_id_PATCH.xs
     *
     * @param {string} patientId
     * @param {Object} patientData
     * @returns {Promise<import('@prism-ehr/shared').Patient>}
     */
    async updatePatient(patientId, patientData) {
      return this.request(`/patient/${patientId}`, {
        method: 'PATCH',
        body: JSON.stringify(patientData)
      });
    },

    /**
     * Delete a patient.
     *
     * Xano: DELETE /patient/{patientId}
     * Xano file: 470_patient_patient_id_DELETE.xs
     *
     * @param {string} patientId
     * @returns {Promise<null>}
     */
    async deletePatient(patientId) {
      return this.request(`/patient/${patientId}`, {
        method: 'DELETE'
      });
    },

    /**
     * Send patient invite (scheduling form link via email).
     *
     * Xano: POST /patient_invite
     * Xano file: 1096_patient_invite_POST.xs
     *
     * @param {Object} inviteData
     * @param {string} inviteData.email
     * @param {string} [inviteData.phone]
     * @param {string} inviteData.organizationId
     * @returns {Promise<Object>}
     */
    async sendPatientInvite(inviteData) {
      return this.request('/patient_invite', {
        method: 'POST',
        body: JSON.stringify({
          email: inviteData.email,
          phone: inviteData.phone || null,
          organization_id: inviteData.organizationId,
          form_ids: inviteData.formIds || [],
          form_id: inviteData.formIds?.[0] || inviteData.formId || null,
        })
      });
    },

    /**
     * Get patient invites for an organization.
     *
     * Xano: GET /patient_invite
     * Xano file: patient_invite_GET.xs
     *
     * @param {string} organizationId
     * @returns {Promise<Object[]>}
     */
    async getPatientInvites(organizationId) {
      return this.request(`/patient_invite?organization_id=${organizationId}`, {
        method: 'GET'
      });
    },

    /**
     * Send a form to a patient via email and/or SMS.
     *
     * Xano: POST /patients/send-intake
     * Xano file: 560_patients_send_intake_POST.xs
     *
     * @param {Object} params
     * @param {string} params.patient_id
     * @param {string} params.organization_id
     * @param {string} params.form_id
     * @param {string[]} params.delivery_methods - e.g. ['email', 'sms']
     * @param {string} [params.email]
     * @param {string} [params.phone]
     * @param {number|null} [params.case_id] - Case the send is scoped to (null = unscoped)
     * @param {string} [params.send_timestamp] - ISO timestamp; when present the backend queues the
     *   send on notification_queue for that time instead of sending inline. Omit to send now.
     * @returns {Promise<Object>}
     */
    async sendPatientIntake({ patient_id, organization_id, form_ids, delivery_methods, email, phone, source, case_id, send_timestamp }) {
      return this.request('/patients/send-intake', {
        method: 'POST',
        body: JSON.stringify({
          patient_id,
          organization_id,
          form_ids,
          form_id: form_ids?.[0] || null,
          delivery_methods,
          email: email || null,
          phone: phone || null,
          source: source || null,
          case_id: case_id ?? null,
          ...(send_timestamp ? { send_timestamp } : {}),
        })
      });
    },

    /**
     * Verify a patient by date of birth (public endpoint).
     *
     * Xano: POST /patient/verify-dob
     * Xano file: not found in staging
     *
     * @param {string} firstName
     * @param {string} lastName
     * @param {string} dob
     * @param {string} formId
     * @param {string} ipAddress
     * @param {string} verificationMethod
     * @param {string} verificationFactor
     * @returns {Promise<import('@prism-ehr/shared').PatientVerificationResult>}
     */
    async verifyPatientByDob(firstName, lastName, dob, formId, ipAddress, verificationMethod, verificationFactor) {
      return this.publicRequest('/patient/verify-dob', {
        method: 'POST',
        body: JSON.stringify({
          first_name: firstName,
          last_name: lastName,
          dob: dob,
          form_id: formId,
          ip_address: ipAddress,
          verification_method: verificationMethod,
          verification_factor: verificationFactor
        })
      });
    },

    /**
     * Create a patient via public endpoint.
     *
     * Xano: POST /public/patient
     * Xano file: 726_public_patient_POST.xs
     *
     * @param {Object} patientData
     * @returns {Promise<import('@prism-ehr/shared').Patient>}
     */
    async createPatientPublic(patientData) {
      return this.publicRequest('/public/patient', {
        method: 'POST',
        body: JSON.stringify({
          first_name: patientData.firstName,
          last_name: patientData.lastName,
          birthday: patientData.dob,
          organization_id: patientData.organizationId,
          phone: patientData.phone,
          email: patientData.email,
          verification_id: patientData.verification_id || null,
          security_question: patientData.securityQuestion || null,
          security_answer: patientData.securityAnswer || null
        })
      });
    },

    /**
     * Send a verification code to a patient (public endpoint).
     *
     * Xano: POST /patient/send-verification-code
     * Xano file: not found in staging
     *
     * @param {string} verificationId
     * @param {string} method
     * @returns {Promise<Object>}
     */
    async sendVerificationCode(verificationId, method) {
      return this.publicRequest('/patient/send-verification-code', {
        method: 'POST',
        body: JSON.stringify({
          verification_id: verificationId,
          method: method
        })
      });
    },

    /**
     * Verify a patient verification code (public endpoint).
     *
     * Xano: POST /patient/verify-code
     * Xano file: not found in staging
     *
     * @param {string} verificationId
     * @param {string} code
     * @param {string} method
     * @returns {Promise<Object>}
     */
    async verifyCode(verificationId, code, method) {
      return this.publicRequest('/patient/verify-code', {
        method: 'POST',
        body: JSON.stringify({
          verification_id: verificationId,
          code: code,
          method: method
        })
      });
    },

    /**
     * Get pending patient updates for review.
     *
     * Xano: GET /pending_patient_update
     * Xano file: 762_pending_patient_update_GET.xs
     *
     * @param {string} patientId
     * @returns {Promise<import('@prism-ehr/shared').PendingPatientUpdate[]>}
     */
    async getPendingPatientUpdates(patientId) {
      return this.request(`/pending_patient_update?patient_id=${patientId}&status=pending`, {
        method: 'GET'
      });
    },

    /**
     * Approve a pending patient update.
     *
     * Xano: POST /pending_patient_update/{updateId}/approve
     * Xano file: not found in staging (may use PATCH endpoint)
     *
     * @param {string} updateId
     * @returns {Promise<import('@prism-ehr/shared').PendingPatientUpdate>}
     */
    async approvePatientUpdate(updateId) {
      return this.request(`/pending_patient_update/${updateId}/approve`, {
        method: 'POST',
        body: JSON.stringify({
          status: 'approved',
          reviewed_at: Date.now()
        })
      });
    },

    /**
     * Reject a pending patient update with reason.
     *
     * Xano: POST /pending_patient_update/{updateId}/reject
     * Xano file: not found in staging (may use PATCH endpoint)
     *
     * @param {string} updateId
     * @param {string} reason
     * @returns {Promise<import('@prism-ehr/shared').PendingPatientUpdate>}
     */
    async rejectPatientUpdate(updateId, reason) {
      return this.request(`/pending_patient_update/${updateId}/reject`, {
        method: 'POST',
        body: JSON.stringify({
          status: 'rejected',
          review_notes: reason,
          reviewed_at: Date.now()
        })
      });
    },

    /**
     * Find an existing patient by matching criteria.
     *
     * Xano: POST /patient_match
     * Xano file: not found in staging
     *
     * @param {Object} data
     * @param {string} data.firstName
     * @param {string} data.lastName
     * @param {string} data.dob
     * @param {string} [data.phone]
     * @param {string} [data.email]
     * @param {string} data.organizationId
     * @returns {Promise<import('@prism-ehr/shared').PatientMatchResult>}
     */
    async findExistingPatient(data) {
      return this.request('/patient_match', {
        method: 'POST',
        body: JSON.stringify({
          first_name: data.firstName,
          last_name: data.lastName,
          date_of_birth: data.dob,
          phone: data.phone,
          email: data.email,
          organization_id: data.organizationId
        })
      });
    },

    /**
     * Create or update a patient from a form submission.
     *
     * Xano: POST /patient_from_form
     * Xano file: not found in staging
     *
     * @param {string} formResponseId
     * @returns {Promise<import('@prism-ehr/shared').Patient>}
     */
    async createOrUpdatePatientFromForm(formResponseId) {
      return this.request('/patient_from_form', {
        method: 'POST',
        body: JSON.stringify({
          form_response_id: formResponseId,
          created_at: Date.now()
        })
      });
    },

    /**
     * Search patients by term with pagination.
     *
     * Xano: GET /search_patients
     * Xano file: 765_search_patients_GET.xs
     *
     * @param {Object} params
     * @param {string} [params.search_term]
     * @param {string} [params.organization_id]
     * @param {number} [params.limit]
     * @param {number} [params.offset]
     * @returns {Promise<import('@prism-ehr/shared').Patient[]>}
     */
    async searchPatients(params) {
      const queryParams = new URLSearchParams();

      if (params.search_term) {
        queryParams.append('search_term', params.search_term);
      }

      if (params.search_name) {
        queryParams.append('search_name', params.search_name);
      }

      if (params.search_dob) {
        queryParams.append('search_dob', params.search_dob);
      }

      if (params.organization_id) {
        queryParams.append('organization_id', params.organization_id);
      }

      if (params.limit) {
        queryParams.append('limit', params.limit);
      }

      if (params.offset) {
        queryParams.append('offset', params.offset);
      }

      return this.request(`/search_patients?${queryParams.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Search patients using the patient endpoint with search query.
     *
     * Xano: GET /patient
     * Xano file: 472_patient_GET.xs
     *
     * @param {string} organizationId
     * @param {string} [query]
     * @returns {Promise<import('@prism-ehr/shared').Patient[]>}
     */
    async searchPatientsExtension(organizationId, query = '') {
      const params = new URLSearchParams({
        organization_id: organizationId
      });
      if (query) {
        params.append('search', query);
      }
      return this.request(`/patient?${params.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Get patient diagnoses (ICD-10 codes from encounter billing).
     *
     * Xano: GET /patient/{patientId}/diagnoses
     * Xano file: 1090_patient_patient_id_diagnoses_GET.xs
     *
     * @param {string} patientId
     * @returns {Promise<Object[]>}
     */
    async getPatientDiagnoses(patientId) {
      return this.request(`/patient/${patientId}/diagnoses`, {
        method: 'GET'
      });
    },

    /**
     * Get admin notes for a patient.
     *
     * Xano: GET /patient-notes
     * Xano file: patient_notes_GET.xs
     *
     * @param {string} patientId
     * @param {string} [noteType]
     * @returns {Promise<Object[]>}
     */
    async getPatientNotes(patientId, noteType) {
      const params = new URLSearchParams({ patient_id: patientId });
      if (noteType) params.append('note_type', noteType);
      return this.request(`/patient-notes?${params}`, { method: 'GET' });
    },

    /**
     * Create an admin note for a patient.
     *
     * Xano: POST /patient-notes
     * Xano file: patient_notes_POST.xs
     *
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async createPatientNote(data) {
      return this.request('/patient-notes', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Update a patient note.
     *
     * Xano: PATCH /patient-notes/{patient_note_id}
     * Xano file: patient_notes_patient_note_id_PATCH.xs
     *
     * @param {string} noteId
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async updatePatientNote(noteId, data) {
      return this.request(`/patient-notes/${noteId}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
    },

    /**
     * Soft-delete a patient note.
     *
     * Xano: DELETE /patient-notes/{patient_note_id}
     * Xano file: patient_notes_patient_note_id_DELETE.xs
     *
     * @param {string} noteId
     * @returns {Promise<Object>}
     */
    async deletePatientNote(noteId) {
      return this.request(`/patient-notes/${noteId}`, {
        method: 'DELETE'
      });
    },

    /**
     * Computed insurance visit-limit usage for a patient. scope 'policy' rows:
     * one per insurance[].visit_limits entry (calendar_year / plan_year counted
     * from completed encounters of cases billing to that entry; lifetime /
     * custom have no window, manual: true). scope 'case' rows:
     * per_condition / per_episode, one per case billing to the entry whose
     * specialty matches, counting that case's encounters inside the benefit year.
     * Benefit year (plan_year + per-case): limit period_start, else policy
     * renewal_date / day after termination_date / effective_date anniversary,
     * else Jan 1; window_source says which. case_ids = cases the row applies to.
     * used_total = used_computed, or when the limit carries a payer-reported total
     * (used_manual = `reported`, given reported_as_of): reported + Prism visits dated on or
     * after that day (used_since_report). A report dated before the benefit window is
     * ignored (reported_stale). Manual rows carry a total only when reported.
     *
     * Xano: GET /patient-visit-limit-usage?patient_id=
     * Xano file: patient-visit-limit-usage_GET.xs
     *
     * @param {string} patientId
     * @returns {Promise<Array<{ insurance_index: number, payer_name: string, specialty: string, allowed: number|null, period: string, scope: 'policy'|'case', case_id: number|null, case_name: string|null, case_status: string|null, case_ids: number[], window_start: number|null, window_source: 'limit'|'renewal_date'|'termination_date'|'effective_date'|'calendar_year'|null, used_computed: number|null, used_manual: number|null, reported_as_of: string|null, used_since_report: number|null, reported_stale: boolean, used_total: number|null, remaining: number|null, manual: boolean }>>}
     */
    async getPatientVisitLimitUsage(patientId) {
      return this.request(`/patient-visit-limit-usage?patient_id=${encodeURIComponent(patientId)}`, {
        method: 'GET'
      });
    },

    /**
     * Upcoming-appointment counts for a batch of patients (Home schedule chip).
     * Patients with no upcoming appointments are omitted from `items`.
     *
     * Xano: POST /patient-upcoming-counts
     * Xano file: patient-upcoming-counts_POST.xs
     *
     * @param {string[]} patientIds - max 100 per call
     * @returns {Promise<{ items: Array<{ patient_id: string, upcoming_count: number }> }>}
     */
    async getPatientUpcomingCounts(patientIds) {
      return this.request('/patient-upcoming-counts', {
        method: 'POST',
        body: JSON.stringify({ patient_ids: (patientIds || []).join(',') }),
      }, 'getPatientUpcomingCounts');
    },

    /**
     * Low insurance benefit visit limits for a batch of patients (Home schedule /
     * calendar chip): rows at or below the org's amber visit-alert threshold,
     * per-case rows for active cases only. Patients with nothing low are absent.
     *
     * Xano: POST /patient-visit-limit-alerts-batch
     * Xano file: patient-visit-limit-alerts-batch_POST.xs
     *
     * @param {string[]} patientIds - max 100 per call
     * @returns {Promise<{ items: Array<{ patient_id: string, case_id: number|null, case_name: string|null, scope: 'policy'|'case', insurance_index: number, specialty: string, payer_name: string, period: string, allowed: number|null, used_total: number|null, remaining: number, severity: 'red'|'amber' }>, thresholds: { amber: number, red: number } }>}
     */
    async getPatientVisitLimitAlertsBatch(patientIds) {
      return this.request('/patient-visit-limit-alerts-batch', {
        method: 'POST',
        body: JSON.stringify({ patient_ids: (patientIds || []).join(',') }),
      }, 'getPatientVisitLimitAlertsBatch');
    },
  };
}
